/* ================= Box It → GA4 =================
   Box It's events (dataLayer.push({event:'bx_*'}) via track()) are sent to GA4 by the
   shared core (assets/bearly-tools-core.js), which forwards bx_* pushes and sends
   through the store's own GA4 tag. Box It no longer loads a second copy of GA4: two
   copies for one measurement ID conflict and one of them loses its events. */

/* Box It — Bearly Art free tool. assets/box-it.js (loaded by sections/box-it.liquid).
   Everything below is scoped in one IIFE so Box It's `$` helper can't clobber
   the theme's jQuery `$` on the Shopify page.
   Download: the button opens the shared popup (BT.runWithProgress in
   assets/bearly-tools-core.js): progress -> email step (only if not unlocked) -> file.
   The core owns the popup, email form, Klaviyo subscription, unlock keys and the
   bx_email_view / bx_email_submit / bx_email_dismiss / bx_progress_cancel / bx_export_error
   events (BT.track pushes them to dataLayer; the bridge above sends them to GA4).
   This file builds the SVG and saves it in the popup's work step (bx_download_click). */
(function(){
'use strict';

/*GEO-START*/
// ---------- geometry core (no DOM) ----------

function signedArea(pts){
  let a=0;
  for(let i=0;i<pts.length;i++){
    const p=pts[i], q=pts[(i+1)%pts.length];
    a += p.x*q.y - q.x*p.y;
  }
  return a/2;
}

function bboxOf(pts){
  let minX=1/0,minY=1/0,maxX=-1/0,maxY=-1/0;
  for(const p of pts){
    if(p.x<minX)minX=p.x; if(p.y<minY)minY=p.y;
    if(p.x>maxX)maxX=p.x; if(p.y>maxY)maxY=p.y;
  }
  return {minX,minY,maxX,maxY,w:maxX-minX,h:maxY-minY};
}

function dedupe(pts, eps){
  const out=[];
  for(const p of pts){
    const last=out[out.length-1];
    if(!last || Math.hypot(p.x-last.x,p.y-last.y)>eps) out.push(p);
  }
  if(out.length>1){
    const f=out[0], l=out[out.length-1];
    if(Math.hypot(f.x-l.x,f.y-l.y)<=eps) out.pop();
  }
  return out;
}

function dropCollinear(pts, tol){
  if(pts.length<4) return pts;
  const out=[];
  const n=pts.length;
  for(let i=0;i<n;i++){
    const a=pts[(i-1+n)%n], b=pts[i], c=pts[(i+1)%n];
    const v1x=b.x-a.x, v1y=b.y-a.y, v2x=c.x-b.x, v2y=c.y-b.y;
    const cross=v1x*v2y-v1y*v2x, dot=v1x*v2x+v1y*v2y;
    const ang=Math.abs(Math.atan2(cross,dot));
    if(ang>tol) out.push(b);
  }
  return out.length>=3?out:pts;
}

function rdp(pts, eps){
  if(pts.length<3) return pts.slice();
  const first=pts[0], last=pts[pts.length-1];
  let idx=-1, maxD=0;
  const dx=last.x-first.x, dy=last.y-first.y;
  const len=Math.hypot(dx,dy)||1e-9;
  for(let i=1;i<pts.length-1;i++){
    const d=Math.abs(dy*(pts[i].x-first.x)-dx*(pts[i].y-first.y))/len;
    if(d>maxD){maxD=d;idx=i;}
  }
  if(maxD>eps){
    const l=rdp(pts.slice(0,idx+1),eps);
    const r=rdp(pts.slice(idx),eps);
    return l.slice(0,-1).concat(r);
  }
  return [first,last];
}

function simplifyClosed(pts, eps){
  if(pts.length<4) return pts.slice();
  // anchor 1: farthest from centroid (a real corner); rotate loop to start there
  let cx=0,cy=0;
  for(const p of pts){cx+=p.x;cy+=p.y;}
  cx/=pts.length;cy/=pts.length;
  let si=0,sd=-1;
  pts.forEach((p,i)=>{const d=Math.hypot(p.x-cx,p.y-cy);if(d>sd){sd=d;si=i;}});
  const rot=pts.slice(si).concat(pts.slice(0,si));
  // anchor 2: farthest from anchor 1 — split there so RDP never sees
  // identical endpoints (a closed loop fed to RDP whole collapses to a point)
  let ai=1,ad=-1;
  rot.forEach((p,i)=>{const d=Math.hypot(p.x-rot[0].x,p.y-rot[0].y);if(d>ad){ad=d;ai=i;}});
  if(ai===0||ai===rot.length-1) ai=Math.floor(rot.length/2);
  const h1=rdp(rot.slice(0,ai+1),eps);
  const h2=rdp(rot.slice(ai).concat([rot[0]]),eps);
  return h1.slice(0,-1).concat(h2.slice(0,-1));
}

function pointInPoly(pt, pts){
  let inside=false;
  const n=pts.length;
  for(let i=0,j=n-1;i<n;j=i++){
    const a=pts[i], b=pts[j];
    if((a.y>pt.y)!==(b.y>pt.y) &&
       pt.x < (b.x-a.x)*(pt.y-a.y)/(b.y-a.y)+a.x) inside=!inside;
  }
  return inside;
}

function segsIntersect(p1,p2,p3,p4){
  const d=(p2.x-p1.x)*(p4.y-p3.y)-(p2.y-p1.y)*(p4.x-p3.x);
  if(Math.abs(d)<1e-12) return false;
  const t=((p3.x-p1.x)*(p4.y-p3.y)-(p3.y-p1.y)*(p4.x-p3.x))/d;
  const u=((p3.x-p1.x)*(p2.y-p1.y)-(p3.y-p1.y)*(p2.x-p1.x))/d;
  return t>1e-9&&t<1-1e-9&&u>1e-9&&u<1-1e-9;
}

function loopsCross(A,B){
  for(let i=0;i<A.length;i++){
    const a1=A[i], a2=A[(i+1)%A.length];
    for(let j=0;j<B.length;j++){
      if(segsIntersect(a1,a2,B[j],B[(j+1)%B.length])) return true;
    }
  }
  return false;
}

// relation of loop B to loop A: 'inside' | 'outside' | 'contains' | 'overlap'
function relation(A,B){
  const ba=bboxOf(A), bb=bboxOf(B);
  if(bb.maxX<ba.minX||bb.minX>ba.maxX||bb.maxY<ba.minY||bb.minY>ba.maxY) return 'outside';
  if(loopsCross(A,B)) return 'overlap';
  const bInA=B.filter(p=>pointInPoly(p,A)).length;
  const aInB=A.filter(p=>pointInPoly(p,B)).length;
  if(bInA===B.length) return 'inside';
  if(aInB===A.length) return 'contains';
  if(bInA===0&&aInB===0) return 'outside';
  return 'overlap';
}

function cornerTypes(pts, invert){
  const n=pts.length;
  const windSign=Math.sign(signedArea(pts))||1;
  const out=[];
  for(let i=0;i<n;i++){
    const a=pts[(i-1+n)%n], b=pts[i], c=pts[(i+1)%n];
    const cross=(b.x-a.x)*(c.y-b.y)-(b.y-a.y)*(c.x-b.x);
    let outer=Math.sign(cross)===windSign;
    if(invert) outer=!outer;
    out.push(outer);
  }
  return out;
}

function segLengths(pts){
  const n=pts.length, out=[];
  for(let i=0;i<n;i++){
    const p=pts[i], q=pts[(i+1)%n];
    out.push(Math.hypot(q.x-p.x,q.y-p.y));
  }
  return out;
}

// merge pass: every segment of the FINAL polygon must be at least minSeg long
// (the glueable minimum). Removes the vertex whose loss distorts the shape
// least, merging its two segments into one chord — faces, folds and tabs all
// derive from the result, so wall and face always agree.
function enforceMinSegment(pts, minSeg){
  const triArea=(a,b,c)=>Math.abs((b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x))/2;
  let out=pts.slice();
  while(out.length>3){
    const n=out.length;
    let si=-1, sl=1/0;
    for(let i=0;i<n;i++){
      const l=Math.hypot(out[(i+1)%n].x-out[i].x, out[(i+1)%n].y-out[i].y);
      if(l<sl){sl=l;si=i;}
    }
    if(sl>=minSeg) break;
    const a=si, b=(si+1)%n;
    const devA=triArea(out[(a-1+n)%n],out[a],out[b]);
    const devB=triArea(out[a],out[b],out[(b+2-1+n)%n]);
    out.splice(devA<=devB?a:b,1);
  }
  // merging can leave near-straight vertices — a fold on a straight line is
  // a pointless notch, so drop them (segments only get longer)
  return dropCollinear(out,0.035);
}

// choose strip split positions (arc-length along the loop):
// - at least `pieces` strips, more if the 11.5in piece cap demands it
// - splits prefer vertices (joint lands on a fold) but may fall mid-segment
//   when a straight run alone exceeds the cap (flat joint, no fold needed)
function chooseSplits(cum, perim, pieces, CAP){
  const n=cum.length-1;
  const N=Math.max(1, Math.max(pieces, Math.ceil(perim/CAP)));
  let splits=[0];
  for(let j=1;j<N;j++){
    const target=j*perim/N;
    let bv=-1,bd=1/0;
    for(let i=1;i<n;i++){const d=Math.abs(cum[i]-target); if(d<bd){bd=d;bv=i;}}
    const tol=Math.min(CAP*0.15,(perim/N)*0.4);
    const pos=(bd<=tol)?cum[bv]:target;
    if(pos>splits[splits.length-1]+1 && pos<perim-1) splits.push(pos);
  }
  splits=[...new Set(splits)].sort((a,b)=>a-b);
  // enforce the cap: walk each strip, inserting the farthest vertex that
  // keeps us under CAP, or a hard split exactly at CAP on a long straight run
  const out=[];
  for(let s=0;s<splits.length;s++){
    const p0=splits[s], p1=(s+1<splits.length)?splits[s+1]:perim;
    out.push(p0);
    let cur=p0;
    while(p1-cur>CAP+1e-9){
      let best=-1;
      for(let i=1;i<n;i++){
        if(cum[i]>cur+1e-6 && cum[i]<=cur+CAP+1e-9 && cum[i]<p1-1e-6) best=Math.max(best,cum[i]);
      }
      cur = best>cur ? best : cur+CAP;
      out.push(cur);
    }
  }
  return out;
}

// wall strips for one closed loop — one tab per segment, always
function buildStrips(pts, pieces, opts, invertCorners){
  const {depth, tabD}=opts;
  const lens=segLengths(pts);
  const perim=lens.reduce((a,b)=>a+b,0);
  const n=pts.length;
  const cum=[0];
  for(let i=0;i<n;i++) cum.push(cum[i]+lens[i]);
  const types=cornerTypes(pts, invertCorners);

  // 11.5in mat cap applies to the whole PIECE: strip length + joint tab
  const CAP=292.1-tabD-2;
  const splits=chooseSplits(cum, perim, Math.min(pieces,n), CAP);
  const ins=tabD*0.7;

  const strips=[];
  const splitMeta=splits.map(pos=>{
    // locate the split: at a vertex, or a point along a segment
    for(let j=0;j<n;j++){
      if(Math.abs(pos-cum[j])<1e-6) return {x:pts[j].x, y:pts[j].y, vert:j, seg:null};
      if(pos>cum[j] && pos<cum[j+1]){
        const t=(pos-cum[j])/lens[j];
        const q=pts[(j+1)%n];
        return {x:pts[j].x+(q.x-pts[j].x)*t, y:pts[j].y+(q.y-pts[j].y)*t, vert:null, seg:j};
      }
    }
    return {x:pts[0].x, y:pts[0].y, vert:0, seg:null};
  });

  for(let s=0;s<splits.length;s++){
    const p0=splits[s];
    const p1=(s+1<splits.length)?splits[s+1]:perim;
    const len=p1-p0;
    const folds=[];
    const bounds=[p0];
    for(let v=1;v<n;v++){
      if(cum[v]>p0+1e-6 && cum[v]<p1-1e-6){
        folds.push({x:cum[v]-p0, outer:types[v]});
        bounds.push(cum[v]);
      }
    }
    bounds.push(p1);
    // one tab per bounded run; over-long runs (rare, straight walls) split
    // into equal adjacent tabs — V-notch, no fold: same wall plane
    const MAXTAB=292.1;
    const tabs=[];
    for(let i=0;i<bounds.length-1;i++){
      const L=bounds[i+1]-bounds[i];
      if(L<3) continue;
      const k=Math.max(1, Math.ceil(L/MAXTAB));
      for(let t=0;t<k;t++){
        tabs.push({x0:bounds[i]-p0+L*t/k, x1:bounds[i]-p0+L*(t+1)/k});
      }
    }
    const endInset=Math.min(ins, Math.max((depth-1)/2, 0.5));
    strips.push({len, folds, tabs, endInset});
  }
  return {strips, perim, splitMeta, splitPts:splitMeta.map(m=>({x:m.x,y:m.y}))};
}

/**
 * Build one shape's pattern: outline + holes.
 * All points already scaled to mm, translated so shape bbox min = (0,0).
 */
function buildShape(outline, holes, opts){
  const rings=[];
  const o=buildStrips(outline, opts.pieces, opts, false);
  rings.push({strips:o.strips, perim:o.perim, splitPts:o.splitPts, splitMeta:o.splitMeta, hole:false});
  for(const h of holes){
    const hp=Math.max(1, Math.min(8, Math.round(opts.pieces * segLengths(h).reduce((a,b)=>a+b,0) / o.perim)));
    const r=buildStrips(h, hp, opts, true);
    rings.push({strips:r.strips, perim:r.perim, splitPts:r.splitPts, splitMeta:r.splitMeta, hole:true});
  }
  const bb=bboxOf(outline);
  const mirror=lp=>lp.map(p=>({x:bb.w-p.x, y:p.y})).reverse();
  return {
    front:{outline, holes},
    back:{outline:mirror(outline), holes:holes.map(mirror)},
    w:bb.w, h:bb.h,
    rings
  };
}
/*GEO-END*/

// ---------- SVG rendering ----------

const CUT='#1e2d7d', FOLD='#0092ac', FOLD_IN='#7c58c9', LABEL='#8b95c2';
// perf: fold lines are REAL short cut segments (machines ignore dasharray
// and would cut a styled line as one continuous slice through the strip).
// solid: single score lines on separate colors for scoring tools.
const FOLD_STYLES={
  perf:  {mode:'perf',  out:{c:3.5,g:2}, in:{c:1.2,g:2}},
  solid: {mode:'solid', out:{color:FOLD}, in:{color:FOLD_IN}}
};

// perforated line: k real dashes spanning exactly (x1,y1)→(x2,y2),
// first and last dash anchored at the ends so the fold reaches its edges
// perforated line: real dashes along (x1,y1)→(x2,y2), inset from both ends
// so no dash touches an intersecting cut line (blade crossings tear)
function perfLine(x1,y1,x2,y2,c0,g0){
  const INSET=1.2;
  const L0=Math.hypot(x2-x1,y2-y1);
  if(L0<=2*INSET+0.8) return '';   // too short to perforate clear of the cuts
  const ux=(x2-x1)/L0, uy=(y2-y1)/L0;
  x1+=ux*INSET; y1+=uy*INSET;
  x2-=ux*INSET; y2-=uy*INSET;
  const L=L0-2*INSET;
  if(L<=c0+0.2){
    return `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" stroke="${CUT}" stroke-width="0.35"/>`;
  }
  let k=Math.max(2, Math.round((L+g0)/(c0+g0)));
  let c=Math.min(c0,(L-0.8*(k-1))/k);
  while(k>2 && c<1){k--; c=Math.min(c0,(L-0.8*(k-1))/k);}
  if(c<0.8){
    return `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" stroke="${CUT}" stroke-width="0.35"/>`;
  }
  const g=(L-k*c)/(k-1);
  let m='';
  for(let i=0;i<k;i++){
    const s=i*(c+g);
    const e=Math.min(s+c,L);
    m+=`<line x1="${(x1+ux*s).toFixed(2)}" y1="${(y1+uy*s).toFixed(2)}" x2="${(x1+ux*e).toFixed(2)}" y2="${(y1+uy*e).toFixed(2)}" stroke="${CUT}" stroke-width="0.35"/>`;
  }
  return m;
}

function foldLineMarkup(x1,y1,x2,y2,kind,FS){
  const st=kind==='out'?FS.out:FS.in;
  if(FS.mode==='perf') return perfLine(x1,y1,x2,y2,st.c,st.g);
  return `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" stroke="${st.color}" stroke-width="0.35"/>`;
}

const ALPHA='ABCDEFGHIJKLMNOPQRSTUVWXYZ';
function jointLetter(i){
  return i<26?ALPHA[i]:ALPHA[Math.floor(i/26)-1]+ALPHA[i%26];
}

function ptsToPath(pts,dx,dy){
  return 'M'+pts.map(p=>(p.x+dx).toFixed(2)+','+(p.y+dy).toFixed(2)).join('L')+'Z';
}

function faceMarkup(face,dx,dy){
  let d=ptsToPath(face.outline,dx,dy);
  for(const h of face.holes) d+=ptsToPath(h,dx,dy);
  return `<path d="${d}" fill="none" stroke="${CUT}" stroke-width="0.4"/>`;
}

function stripMarkup(strip, T, D, ox, oy, letterEnd, letterStart, FS){
  const {len, folds, tabs, endInset}=strip;
  let d=`M${ox},${oy}`;
  for(const t of tabs){
    const insEff=Math.min(D*0.7,(t.x1-t.x0)/2-0.5);
    d+=`L${(ox+t.x0).toFixed(2)},${oy}`;
    d+=`L${(ox+t.x0+insEff).toFixed(2)},${(oy-D).toFixed(2)}`;
    d+=`L${(ox+t.x1-insEff).toFixed(2)},${(oy-D).toFixed(2)}`;
    d+=`L${(ox+t.x1).toFixed(2)},${oy}`;
  }
  d+=`L${(ox+len).toFixed(2)},${oy}`;
  d+=`L${(ox+len+D).toFixed(2)},${(oy+endInset).toFixed(2)}`;
  d+=`L${(ox+len+D).toFixed(2)},${(oy+T-endInset).toFixed(2)}`;
  d+=`L${(ox+len).toFixed(2)},${(oy+T).toFixed(2)}`;
  for(let i=tabs.length-1;i>=0;i--){
    const t=tabs[i];
    const insEff=Math.min(D*0.7,(t.x1-t.x0)/2-0.5);
    d+=`L${(ox+t.x1).toFixed(2)},${(oy+T).toFixed(2)}`;
    d+=`L${(ox+t.x1-insEff).toFixed(2)},${(oy+T+D).toFixed(2)}`;
    d+=`L${(ox+t.x0+insEff).toFixed(2)},${(oy+T+D).toFixed(2)}`;
    d+=`L${(ox+t.x0).toFixed(2)},${(oy+T).toFixed(2)}`;
  }
  d+=`L${ox},${(oy+T).toFixed(2)}`;
  d+='Z';

  let piece=`<path d="${d}" fill="none" stroke="${CUT}" stroke-width="0.4"/>`;
  for(const f of folds){
    piece+=foldLineMarkup(ox+f.x, oy, ox+f.x, oy+T, f.outer?'out':'in', FS);
  }
  piece+=foldLineMarkup(ox+len, oy, ox+len, oy+T, 'out', FS);
  for(const t of tabs){
    piece+=foldLineMarkup(ox+t.x0, oy, ox+t.x1, oy, 'out', FS);
    piece+=foldLineMarkup(ox+t.x0, oy+T, ox+t.x1, oy+T, 'out', FS);
  }
  const fs=Math.max(4,Math.min(7,T*0.4));
  // joint letters live OUTSIDE the strip geometry (waste area)
  let labels=`<text x="${(ox+len+D+2.5).toFixed(2)}" y="${(oy+T/2).toFixed(2)}" font-size="${fs}" fill="${LABEL}" text-anchor="start" dominant-baseline="middle" font-family="Archivo,sans-serif">${letterEnd}</text>`;
  labels+=`<text x="${(ox-2.5).toFixed(2)}" y="${(oy+T/2).toFixed(2)}" font-size="${fs}" fill="${LABEL}" text-anchor="end" dominant-baseline="middle" font-family="Archivo,sans-serif">${letterStart}</text>`;
  return {piece, labels};
}

function renderSVG(shapes, T, D, dimLabel, foldStyle, withBackground){
  const M=10, GAP=10;
  const FS=FOLD_STYLES[foldStyle]||FOLD_STYLES.perf;
  let g='', labels='', y=M+(dimLabel?16:0), maxW=0, jointIdx=0;

  if(dimLabel && shapes.length){
    const w=shapes[0].w, yd=y-11;
    labels+=`<g stroke="${LABEL}" stroke-width="0.35" fill="none">`+
       `<line x1="${M}" y1="${yd}" x2="${(M+w).toFixed(1)}" y2="${yd}"/>`+
       `<line x1="${M}" y1="${(yd-2.5).toFixed(1)}" x2="${M}" y2="${(yd+2.5).toFixed(1)}"/>`+
       `<line x1="${(M+w).toFixed(1)}" y1="${(yd-2.5).toFixed(1)}" x2="${(M+w).toFixed(1)}" y2="${(yd+2.5).toFixed(1)}"/>`+
       `</g>`+
       `<text x="${(M+w/2).toFixed(1)}" y="${(yd-1.8).toFixed(1)}" font-size="5" fill="${LABEL}" text-anchor="middle" font-family="Archivo,sans-serif">${dimLabel}</text>`;
  }

  shapes.forEach((sh,si)=>{
    // faces row — each face is its own group
    g+=`<g id="front-${si+1}">${faceMarkup(sh.front,M,y)}</g>`;
    const backX=M+sh.w+GAP;
    g+=`<g id="back-${si+1}">${faceMarkup(sh.back,backX,y)}</g>`;
    // joint letters on the front face: placed OFF the material — outside the
    // main outline, inside a hole's void — via local bisector, verified with
    // a point-in-polygon test (centroid nudging fails on concave shapes)
    let mBase=jointIdx;
    sh.rings.forEach((ring,ri)=>{
      const loop = ri===0 ? sh.front.outline : sh.front.holes[ri-1];
      const n=ring.strips.length;
      const ln=loop.length;
      ring.splitMeta.forEach((sm,i)=>{
        const L=jointLetter(mBase+((i-1+n)%n));
        const p={x:sm.x, y:sm.y};
        // candidate directions: bisector (or segment normal), then each
        // adjacent edge normal — thin regions can defeat any single guess
        const dirs=[];
        const push=(dx,dy)=>{const l=Math.hypot(dx,dy)||1; dirs.push([dx/l,dy/l],[-dx/l,-dy/l]);};
        if(sm.vert!==null){
          const a=loop[(sm.vert-1+ln)%ln], c=loop[(sm.vert+1)%ln];
          const d1x=a.x-p.x, d1y=a.y-p.y, l1=Math.hypot(d1x,d1y)||1;
          const d2x=c.x-p.x, d2y=c.y-p.y, l2=Math.hypot(d2x,d2y)||1;
          const bx=d1x/l1+d2x/l2, by=d1y/l1+d2y/l2;
          if(Math.hypot(bx,by)>1e-6) push(bx,by);
          push(-d1y/l1,d1x/l1);
          push(-d2y/l2,d2x/l2);
        } else {
          const a=loop[sm.seg], c=loop[(sm.seg+1)%ln];
          push(-(c.y-a.y),(c.x-a.x));
        }
        const wantInside = ri>0;   // main shape: outside; hole: inside the void
        let tx=null,ty=null;
        outer:
        for(const OFF of [6.5,9,3.8]){   // prefer room for the arrow
          for(const [dx,dy] of dirs){
            const cx2=p.x+dx*OFF, cy2=p.y+dy*OFF;
            if(pointInPoly({x:cx2,y:cy2},loop)===wantInside){tx=cx2;ty=cy2;break outer;}
          }
        }
        if(tx===null){tx=p.x+dirs[0][0]*6.5;ty=p.y+dirs[0][1]*6.5;}
        labels+=`<text x="${(tx+M).toFixed(2)}" y="${(ty+y).toFixed(2)}" font-size="4.2" fill="${LABEL}" text-anchor="middle" dominant-baseline="middle" font-family="Archivo,sans-serif">${L}</text>`;
        // direction arrow: the way the strip travels from this joint
        {
          let vx,vy;
          if(sm.vert!==null){
            const q=loop[(sm.vert+1)%ln];
            vx=q.x-p.x; vy=q.y-p.y;
          } else {
            const a=loop[sm.seg], q=loop[(sm.seg+1)%ln];
            vx=q.x-a.x; vy=q.y-a.y;
          }
          const vl=Math.hypot(vx,vy)||1;
          const ux2=vx/vl, uy2=vy/vl;
          const sx=tx+ux2*2.6, sy=ty+uy2*2.6;   // start clear of the glyph
          const ex=sx+ux2*4.5, ey=sy+uy2*4.5;
          labels+=`<line x1="${(sx+M).toFixed(2)}" y1="${(sy+y).toFixed(2)}" x2="${(ex+M).toFixed(2)}" y2="${(ey+y).toFixed(2)}" stroke="${LABEL}" stroke-width="0.4"/>`;
          const HS=1.2, ca=Math.cos(0.5), sa=Math.sin(0.5);
          const h1x=ex-(ux2*ca-uy2*sa)*HS, h1y=ey-(uy2*ca+ux2*sa)*HS;
          const h2x=ex-(ux2*ca+uy2*sa)*HS, h2y=ey-(uy2*ca-ux2*sa)*HS;
          labels+=`<line x1="${(h1x+M).toFixed(2)}" y1="${(h1y+y).toFixed(2)}" x2="${(ex+M).toFixed(2)}" y2="${(ey+y).toFixed(2)}" stroke="${LABEL}" stroke-width="0.4"/>`;
          labels+=`<line x1="${(h2x+M).toFixed(2)}" y1="${(h2y+y).toFixed(2)}" x2="${(ex+M).toFixed(2)}" y2="${(ey+y).toFixed(2)}" stroke="${LABEL}" stroke-width="0.4"/>`;
        }
      });
      mBase+=n;
    });
    if(backX+sh.w>maxW)maxW=backX+sh.w;
    y+=sh.h+14+D;

    // strip rows — each strip is one group holding its cut path AND its
    // perforations, so the piece moves/selects as a unit in cutting software
    for(const ring of sh.rings){
      const n=ring.strips.length;
      const base=jointIdx;
      ring.strips.forEach((s,i)=>{
        const endL=jointLetter(base+i);
        const startL=jointLetter(base+((i-1+n)%n));
        const sm=stripMarkup(s,T,D,M,y,endL,startL,FS);
        g+=`<g id="strip-${endL}">${sm.piece}</g>`;
        labels+=sm.labels;
        const w=M+s.len+D+9;
        if(w>maxW)maxW=w;
        y+=T+2*D+8;
      });
      jointIdx+=n;
    }
    y+=6; // gap between shape blocks
  });

  const W=(maxW+M).toFixed(1), H=(y-2*D+M-14+D).toFixed(1);
  const bg=withBackground?`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`:'';
  return {markup:`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}mm" height="${H}mm" font-family="Archivo,sans-serif">${bg}${g}<g id="labels">${labels}</g></svg>`, W:+W, H:+H};
}

// ---------- SVG import: collect ALL closed loops ----------

/*PARSE-START*/
// Self-contained SVG path parser — no browser measurement APIs
// (WebKit can return 0 from getTotalLength on non-rendered SVGs).
// Supports M L H V C S Q T A Z, absolute + relative, implicit repeats,
// crammed arc flags, scientific notation.

function tokenizePath(d){
  const toks=[];
  let i=0;
  const isCmd=c=>/[MmLlHhVvCcSsQqTtAaZz]/.test(c);
  const numRe=/^[+-]?(?:\d*\.\d+|\d+\.?\d*)(?:[eE][+-]?\d+)?/;
  while(i<d.length){
    const c=d[i];
    if(isCmd(c)){toks.push({cmd:c});i++;continue;}
    if(/[\s,]/.test(c)){i++;continue;}
    const m=d.slice(i).match(numRe);
    if(m && m[0].length){toks.push({num:parseFloat(m[0]), raw:m[0]});i+=m[0].length;continue;}
    i++; // skip anything unrecognised
  }
  return toks;
}

function sampleCubic(p0,p1,p2,p3,out,steps){
  for(let i=1;i<=steps;i++){
    const t=i/steps, u=1-t;
    out.push({
      x:u*u*u*p0.x+3*u*u*t*p1.x+3*u*t*t*p2.x+t*t*t*p3.x,
      y:u*u*u*p0.y+3*u*u*t*p1.y+3*u*t*t*p2.y+t*t*t*p3.y
    });
  }
}

function sampleQuad(p0,p1,p2,out,steps){
  for(let i=1;i<=steps;i++){
    const t=i/steps, u=1-t;
    out.push({
      x:u*u*p0.x+2*u*t*p1.x+t*t*p2.x,
      y:u*u*p0.y+2*u*t*p1.y+t*t*p2.y
    });
  }
}

// SVG spec F.6.5 endpoint→center arc conversion, then sample
function sampleArc(p0,rx,ry,rotDeg,largeArc,sweep,p1,out,steps){
  if(rx===0||ry===0||(p0.x===p1.x&&p0.y===p1.y)){out.push({x:p1.x,y:p1.y});return;}
  rx=Math.abs(rx);ry=Math.abs(ry);
  const phi=rotDeg*Math.PI/180, cosP=Math.cos(phi), sinP=Math.sin(phi);
  const dx2=(p0.x-p1.x)/2, dy2=(p0.y-p1.y)/2;
  const x1p=cosP*dx2+sinP*dy2, y1p=-sinP*dx2+cosP*dy2;
  const lam=x1p*x1p/(rx*rx)+y1p*y1p/(ry*ry);
  if(lam>1){const s=Math.sqrt(lam);rx*=s;ry*=s;}
  const sign=(largeArc!==sweep)?1:-1;
  const num=rx*rx*ry*ry-rx*rx*y1p*y1p-ry*ry*x1p*x1p;
  const den=rx*rx*y1p*y1p+ry*ry*x1p*x1p;
  const co=sign*Math.sqrt(Math.max(0,num/den));
  const cxp=co*rx*y1p/ry, cyp=-co*ry*x1p/rx;
  const cx=cosP*cxp-sinP*cyp+(p0.x+p1.x)/2;
  const cy=sinP*cxp+cosP*cyp+(p0.y+p1.y)/2;
  const ang=(ux,uy,vx,vy)=>{
    const dot=ux*vx+uy*vy, len=Math.hypot(ux,uy)*Math.hypot(vx,vy);
    let a=Math.acos(Math.max(-1,Math.min(1,dot/len)));
    if(ux*vy-uy*vx<0)a=-a;
    return a;
  };
  const t1=ang(1,0,(x1p-cxp)/rx,(y1p-cyp)/ry);
  let dt=ang((x1p-cxp)/rx,(y1p-cyp)/ry,(-x1p-cxp)/rx,(-y1p-cyp)/ry)%(2*Math.PI);
  if(!sweep&&dt>0)dt-=2*Math.PI;
  if(sweep&&dt<0)dt+=2*Math.PI;
  for(let i=1;i<=steps;i++){
    const a=t1+dt*i/steps;
    out.push({
      x:cx+rx*Math.cos(a)*cosP-ry*Math.sin(a)*sinP,
      y:cy+rx*Math.cos(a)*sinP+ry*Math.sin(a)*cosP
    });
  }
}

function parsePathD(d){
  const toks=tokenizePath(d);
  const loops=[];
  let cur=[], pos={x:0,y:0}, start={x:0,y:0};
  let prevCmd='', prevCtrl=null;
  let ti=0;
  const CS=24; // curve samples

  const flush=()=>{if(cur.length>2)loops.push(cur);cur=[];};
  const nextNum=()=>{
    if(ti<toks.length&&toks[ti].num!==undefined)return toks[ti++].num;
    return null;
  };
  // arc flags may be crammed ("011" = flags 0,1 + start of number 1)
  const nextFlag=()=>{
    if(ti>=toks.length||toks[ti].num===undefined)return null;
    const t=toks[ti];
    const raw=t.raw;
    if(raw.length===1){ti++;return raw==='1'?1:0;}
    const f=raw[0]==='1'?1:0;
    const rest=raw.slice(1);
    toks[ti]={num:parseFloat(rest), raw:rest};
    return f;
  };
  const peekNum=()=>ti<toks.length&&toks[ti].num!==undefined;

  while(ti<toks.length){
    const t=toks[ti];
    let cmd;
    if(t.cmd!==undefined){cmd=t.cmd;ti++;}
    else if(prevCmd){cmd=(prevCmd==='M')?'L':(prevCmd==='m')?'l':prevCmd;}
    else {ti++;continue;}
    const rel=cmd===cmd.toLowerCase();
    const C=cmd.toUpperCase();

    if(C==='Z'){
      flush();
      pos={...start};
      prevCmd=cmd;prevCtrl=null;
      continue;
    }
    if(C==='M'){
      const x=nextNum(),y=nextNum();
      if(x===null||y===null)break;
      flush();
      pos=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      start={...pos};
      cur.push({...pos});
      prevCmd=cmd;prevCtrl=null;
      continue;
    }
    if(C==='L'){
      const x=nextNum(),y=nextNum();
      if(x===null||y===null)break;
      pos=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      cur.push({...pos});
      prevCmd=cmd;prevCtrl=null;
      continue;
    }
    if(C==='H'){
      const x=nextNum();
      if(x===null)break;
      pos={x:rel?pos.x+x:x, y:pos.y};
      cur.push({...pos});
      prevCmd=cmd;prevCtrl=null;
      continue;
    }
    if(C==='V'){
      const y=nextNum();
      if(y===null)break;
      pos={x:pos.x, y:rel?pos.y+y:y};
      cur.push({...pos});
      prevCmd=cmd;prevCtrl=null;
      continue;
    }
    if(C==='C'){
      const x1=nextNum(),y1=nextNum(),x2=nextNum(),y2=nextNum(),x=nextNum(),y=nextNum();
      if(y===null)break;
      const c1=rel?{x:pos.x+x1,y:pos.y+y1}:{x:x1,y:y1};
      const c2=rel?{x:pos.x+x2,y:pos.y+y2}:{x:x2,y:y2};
      const end=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      sampleCubic(pos,c1,c2,end,cur,CS);
      pos=end;prevCtrl=c2;prevCmd=cmd;
      continue;
    }
    if(C==='S'){
      const x2=nextNum(),y2=nextNum(),x=nextNum(),y=nextNum();
      if(y===null)break;
      const pc=('CcSs'.includes(prevCmd)&&prevCtrl)?{x:2*pos.x-prevCtrl.x,y:2*pos.y-prevCtrl.y}:{...pos};
      const c2=rel?{x:pos.x+x2,y:pos.y+y2}:{x:x2,y:y2};
      const end=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      sampleCubic(pos,pc,c2,end,cur,CS);
      pos=end;prevCtrl=c2;prevCmd=cmd;
      continue;
    }
    if(C==='Q'){
      const x1=nextNum(),y1=nextNum(),x=nextNum(),y=nextNum();
      if(y===null)break;
      const c1=rel?{x:pos.x+x1,y:pos.y+y1}:{x:x1,y:y1};
      const end=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      sampleQuad(pos,c1,end,cur,CS);
      pos=end;prevCtrl=c1;prevCmd=cmd;
      continue;
    }
    if(C==='T'){
      const x=nextNum(),y=nextNum();
      if(y===null)break;
      const c1=('QqTt'.includes(prevCmd)&&prevCtrl)?{x:2*pos.x-prevCtrl.x,y:2*pos.y-prevCtrl.y}:{...pos};
      const end=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      sampleQuad(pos,c1,end,cur,CS);
      pos=end;prevCtrl=c1;prevCmd=cmd;
      continue;
    }
    if(C==='A'){
      const rx=nextNum(),ry=nextNum(),rot=nextNum();
      const laf=nextFlag(),swf=nextFlag();
      const x=nextNum(),y=nextNum();
      if(y===null||laf===null||swf===null)break;
      const end=rel?{x:pos.x+x,y:pos.y+y}:{x,y};
      sampleArc(pos,rx,ry,rot,!!laf,!!swf,end,cur,CS);
      pos=end;prevCtrl=null;prevCmd=cmd;
      continue;
    }
    // unknown command: skip its numbers
    while(peekNum())ti++;
    prevCmd='';
  }
  flush();
  return loops;
}
/*PARSE-END*/

function samplePathLoops(d){
  return parsePathD(d).map(lp=>{
    const bb=bboxOf(lp);
    const eps=Math.max(bb.w,bb.h)*0.006;
    return dropCollinear(dedupe(simplifyClosed(dedupe(lp,1e-6),eps),eps*0.5),0.02);
  });
}

function elementLoops(el){
  const tag=el.tagName.toLowerCase();
  if(tag==='polygon'||tag==='polyline'){
    const nums=(el.getAttribute('points')||'').trim().split(/[\s,]+/).map(Number);
    const pts=[];
    for(let i=0;i+1<nums.length;i+=2) pts.push({x:nums[i],y:nums[i+1]});
    return [pts];
  }
  if(tag==='rect'){
    const x=+el.getAttribute('x')||0, y=+el.getAttribute('y')||0;
    const w=+el.getAttribute('width')||0, h=+el.getAttribute('height')||0;
    return [[{x,y},{x:x+w,y},{x:x+w,y:y+h},{x,y:y+h}]];
  }
  if(tag==='circle'||tag==='ellipse'){
    const cx=+el.getAttribute('cx')||0, cy=+el.getAttribute('cy')||0;
    const rx=tag==='circle'?(+el.getAttribute('r')||0):(+el.getAttribute('rx')||0);
    const ry=tag==='circle'?rx:(+el.getAttribute('ry')||0);
    const pts=[];
    for(let i=0;i<48;i++){
      const a=i/48*2*Math.PI;
      pts.push({x:cx+rx*Math.cos(a), y:cy+ry*Math.sin(a)});
    }
    return [pts];
  }
  if(tag==='path'){
    return samplePathLoops(el.getAttribute('d')||'');
  }
  return [];
}

function importSVG(text){
  // Shared SVG cleaner (bearly-tools-core.js): drop DOCTYPE/entities, then strip anything that
  // can run code or reach the network. Box It only reads shapes, so nothing it uses is removed.
  const clean=window.BT && BT.cleanSvgText
    ? BT.cleanSvgText(text)
    : String(text==null?'':text).replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/i,'');
  const doc=new DOMParser().parseFromString(clean,'image/svg+xml');
  if(doc.querySelector('parsererror')) throw new Error('That file is not a valid SVG.');
  if(window.BT && BT.sanitizeSvg) BT.sanitizeSvg(doc.documentElement);
  const root=doc.querySelector('svg');
  let vbW=0,vbH=0;
  if(root && root.getAttribute('viewBox')){
    const vb=root.getAttribute('viewBox').trim().split(/[\s,]+/).map(Number);
    vbW=vb[2]||0; vbH=vb[3]||0;
  }
  const els=[...doc.querySelectorAll('path,polygon,polyline,rect,circle,ellipse')];
  if(!els.length){
    // say what the file holds instead, so people know what to change
    if(doc.querySelector('image')) throw new Error('This SVG holds a picture, not cut lines. Trace it into shapes in your design software, then upload it again.');
    if(doc.querySelector('text')) throw new Error('The text in this SVG is still live text. Convert it to outlines (shapes) in your design software, then upload it again.');
    throw new Error('No shapes found — the SVG needs a path, polygon, rect, or circle.');
  }

  let found=[];
  for(const el of els){
    for(let pts of elementLoops(el)){
      pts=dedupe(pts,1e-6);
      if(pts.length<3) continue;
      const bb=bboxOf(pts);
      const eps=Math.max(bb.w,bb.h)*0.004;
      pts=dropCollinear(dedupe(pts,eps*0.5),0.02);
      // very detailed loops: smooth them down to the point budget instead of skipping them
      for(let k=2;pts.length>MAX_LOOP_PTS&&k<=64;k*=2) pts=dropCollinear(dedupe(simplifyClosed(pts,eps*k),eps*0.5),0.02);
      if(pts.length<3||pts.length>MAX_LOOP_PTS) continue;
      const area=Math.abs(signedArea(pts));
      if(area<1e-6) continue;
      // only a true background RECTANGLE counts as canvas — a tight-cropped
      // export makes any shape viewBox-sized, and shapes must stay eligible
      const rectLike = pts.length===4 && Math.abs(area-bb.w*bb.h)<bb.w*bb.h*0.05;
      const canvas = rectLike && vbW>0 && Math.abs(bb.w-vbW)<vbW*0.02 && Math.abs(bb.h-vbH)<vbH*0.02;
      found.push({pts, area, bbox:bboxOf(pts), canvas});
    }
  }
  if(!found.length) throw new Error('Could not trace any outlines from that file.');
  return keepLargest(found);
}

// A busy file (a word, a detailed design) used to be refused outright. Keep the biggest
// outlines instead: specks under 0.2% of the largest shape go first, then anything past
// MAX_LOOPS. found.total / found.dropped say what was left out.
const MAX_LOOPS=40, MAX_LOOP_PTS=120;
function keepLargest(found){
  const total=found.length;
  const shapes=found.filter(f=>!f.canvas);
  const biggest=Math.max(...(shapes.length?shapes:found).map(f=>f.area));
  let kept=found.filter(f=>f.canvas||f.area>=biggest*0.002);
  if(kept.length>MAX_LOOPS) kept=kept.slice().sort((a,b)=>b.area-a.area).slice(0,MAX_LOOPS);
  // keep the file's order so the picker and ids stay stable
  kept=found.filter(f=>kept.includes(f));
  kept.total=total;
  kept.dropped=total-kept.length;
  return kept;
}

// ---------- sample ----------

const SAMPLE_SVG=`<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
<svg width="100%" height="100%" viewBox="0 0 2400 2866" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:2;">
    <g transform="matrix(1,0,0,1,-384.746,-111.709)">
        <g transform="matrix(2.79497,0,0,2.79497,-4134.68,-1738.53)">
            <path d="M1616.99,661.99L2026.75,661.99C2107.86,661.99 2168.35,665.369 2208.2,672.129C2248.06,678.888 2283.72,692.99 2315.19,714.434C2346.66,735.877 2372.88,764.43 2393.86,800.092C2414.83,835.754 2425.32,875.728 2425.32,920.015C2425.32,968.03 2412.39,1012.08 2386.51,1052.17C2360.64,1092.26 2325.56,1122.33 2281.28,1142.38C2343.74,1160.56 2391.76,1191.56 2425.32,1235.38C2458.89,1279.2 2475.67,1330.71 2475.67,1389.91C2475.67,1436.53 2464.83,1481.87 2443.15,1525.92C2421.48,1569.97 2391.88,1605.17 2354.35,1631.51C2316.82,1657.85 2270.55,1674.04 2215.55,1680.1C2181.05,1683.83 2097.84,1686.16 1965.91,1687.1L1616.99,1687.1L1616.99,661.99ZM1823.96,832.608L1823.96,1069.65L1959.62,1069.65C2040.27,1069.65 2090.38,1068.49 2109.96,1066.16C2145.39,1061.96 2173.24,1049.73 2193.52,1029.45C2213.8,1009.17 2223.94,982.481 2223.94,949.383C2223.94,917.684 2215.2,891.928 2197.72,872.116C2180.23,852.303 2154.25,840.3 2119.75,836.104C2099.24,833.773 2040.27,832.608 1942.84,832.608L1823.96,832.608ZM1823.96,1240.27L1823.96,1514.38L2015.56,1514.38C2090.15,1514.38 2137.46,1512.28 2157.51,1508.09C2188.28,1502.49 2213.33,1488.86 2232.68,1467.18C2252.03,1445.5 2261.7,1416.49 2261.7,1380.12C2261.7,1349.36 2254.24,1323.25 2239.32,1301.81C2224.4,1280.36 2202.84,1264.75 2174.64,1254.96C2146.44,1245.17 2085.25,1240.27 1991.09,1240.27L1823.96,1240.27Z" style="fill-rule:nonzero;"/>
        <\/g>
    <\/g>
<\/svg>`;

function loadSample(){
  inputSource='sample'; lastFileName='';
  setLoops(importSVG(SAMPLE_SVG), 'B');
}
function loadSampleClick(){
  loadSample();
  trackStarted();
}

// ---------- app state ----------

const $=id=>document.getElementById('bx-'+id);
let loops=[];        // {id, pts, area, bbox, canvas, role, rel}
let primaryId=0;
let shapeLabel='Star';
let unit='in';
let foldStyle='perf';
const IN=25.4;
let lastSVG=null;

// ---------- tracking + Klaviyo events ----------
/* The email step, list subscription and shared unlock live in the core
   (BT.runWithProgress). Klaviyo events go through BT.klaviyoEvent (known email
   only; it adds tool + tool_handle): "Started Box It" when a shape/sample is loaded
   (or, if the email only becomes known in the popup, just before "Used Box It"),
   "Used Box It" when the file is saved. */
const TOOL_NAME='Box It';

let inputSource='sample';     // 'sample' | 'upload' | 'letter' (box-it-letters.js)
let lastFileName='';
let lastStats=null;           // set by update()
let customizedSent=false;
let startedSent=false;        // "Started Box It" goes once per page, once the email is known

function track(event,params){
  (window.dataLayer=window.dataLayer||[]).push(Object.assign({event},params||{}));
}
function lsGet(k){ try{ return localStorage.getItem(k); }catch(e){ return null; } }

function isUnlocked(){
  // blocked storage must NOT count as unlocked
  return lsGet('ba_tools_unlocked')==='1' || lsGet('ps_unlocked')==='1';
}
function signedInCustomer(){
  if(window.BX_CUSTOMER&&window.BX_CUSTOMER.email) return true;
  const sa=window.ShopifyAnalytics&&window.ShopifyAnalytics.meta&&window.ShopifyAnalytics.meta.page;
  return !!((sa&&sa.customerId)||(window.__st&&window.__st.cid));
}

function klaviyoEvent(name,props){
  try{
    if(window.BT&&typeof window.BT.klaviyoEvent==='function') return window.BT.klaviyoEvent(name,props);
  }catch(e){}
  return Promise.resolve(false);
}
function trackStarted(){
  if(startedSent) return;
  const BT=window.BT;
  if(!(BT&&typeof BT.email==='function'&&BT.email())) return;   // no email yet: try again on the next load
  startedSent=true;
  klaviyoEvent('Started Box It',{input_source:inputSource,file_name:inputSource==='sample'?'sample-B':lastFileName});
}

function usageProps(){
  const s=lastStats||{};
  return {
    tool:TOOL_NAME,
    input_source:inputSource,
    file_name:inputSource==='sample'?'sample-B':lastFileName,
    unit,
    fold_style:foldStyle,
    width_in:s.widthMM?+(s.widthMM/IN).toFixed(3):null,
    depth_in:s.depthMM?+(s.depthMM/IN).toFixed(3):null,
    strips:s.strips||0,
    holes:s.holes||0,
    shapes:s.shapes||0
  };
}

function markCustomized(what){
  if(customizedSent) return;
  customizedSent=true;
  const p=usageProps();
  track('bx_shape_customized',{control:what,input_source:p.input_source});
}

// ---------- download (shared popup from the core) ----------
/* Download click -> BT.runWithProgress: progress card -> email step (unless unlocked /
   signed in) -> doDownload saves the file. The ×, Escape and a backdrop click close it
   WITHOUT the file; the shape and settings stay as they are; the next download asks again. */
let procRunning=false;

function runDownload(){
  if(!lastSVG||procRunning) return;
  if(!(window.BT&&window.BT.runWithProgress)){ doDownload(); return; }   // old core cached: no popup
  procRunning=true;
  const s=lastStats||{};
  window.BT.runWithProgress({
    stages:[
      {p:30, wait:500, text:'Tracing your outline\u2026'},
      {p:58, wait:750, text:'Laying out '+(s.strips||0)+' wall strip'+(s.strips===1?'':'s')+'\u2026'},
      {p:84, wait:750, text:'Adding fold perforations\u2026'}
    ],
    gateText:'Almost there \u2014 where should we send your crafting tips?',
    doneText:'Your file is ready!',
    work:doDownload,
    onEnd:r=>{
      if(r.status!=='busy') procRunning=false;
      if(r.status==='dismissed'){ const dl=$('download'); if(dl){ try{ dl.focus(); }catch(e){} } }
    }
  }).then(r=>{ if(r.status==='busy') procRunning=false; });
}

function doDownload(){
  if(!lastSVG) return;
  const svg=window.BT&&BT.trueSize?BT.trueSize(lastSVG):lastSVG; // 72 units/in for Design Space
  const blob=new Blob([svg],{type:'image/svg+xml'});
  const a=document.createElement('a');
  a.href=URL.createObjectURL(blob);
  a.download=(window.BT && BT.brandName ? BT.brandName(shapeLabel.replace(/\s+/g,'-').toLowerCase()+'-box-it.svg') : shapeLabel.replace(/\s+/g,'-').toLowerCase()+'-box-it.svg');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(()=>URL.revokeObjectURL(a.href),1000);
  const p=usageProps();
  track('bx_download_click',{input_source:p.input_source,width_in:p.width_in,depth_in:p.depth_in,
    strips:p.strips,holes:p.holes,fold_style:p.fold_style,unit:p.unit});
  trackStarted();   // first time the email is known on this page (e.g. just given in the popup)
  klaviyoEvent('Used Box It',p);
}

function fmt(mm){
  return unit==='mm' ? Math.round(mm)+' mm' : (+(mm/IN).toFixed(4))+'\u2033';
}

function setLoops(found,label){
  loops=found.map((f,i)=>({id:i, ...f, role:'ignore', rel:'outside'}));
  // primary = largest non-canvas loop (fall back to largest overall)
  const candidates=loops.filter(l=>!l.canvas);
  const pool=candidates.length?candidates:loops;
  primaryId=pool.reduce((a,b)=>b.area>a.area?b:a).id;
  reclassify();
  shapeLabel=label;
  $('shapeName').innerHTML='Shape: <b>'+label+'</b>';
  $('err').classList.remove('show');
  renderPicker();
  update();
}

function reclassify(){
  const P=loops.find(l=>l.id===primaryId);
  if(!P){
    for(const l of loops){l.role='ignore';l.rel='none';}
    $('blockNote').hidden=true;
    return;
  }
  let blocked=false;
  for(const l of loops){
    if(l.id===primaryId){l.role='outline';l.rel='self';continue;}
    l.rel=relation(P.pts,l.pts);
    if(l.rel==='overlap'||l.rel==='contains'){l.role='blocked';blocked=true;}
    else if(l.rel==='inside'){l.role='hole';}
    else {l.role='ignore';}
  }
  // holes inside holes → block (v2 keeps one nesting level)
  const holes=loops.filter(l=>l.role==='hole');
  for(const h of holes){
    for(const h2 of holes){
      if(h!==h2 && relation(h.pts,h2.pts)==='inside'){h2.role='blocked';blocked=true;}
    }
  }
  $('blockNote').hidden=!blocked;
}

function renderPicker(){
  const wrap=$('pickerWrap');
  if(loops.length<2){wrap.hidden=true;return;}
  wrap.hidden=false;
  let minX=1/0,minY=1/0,maxX=-1/0,maxY=-1/0;
  for(const l of loops){
    if(l.bbox.minX<minX)minX=l.bbox.minX;
    if(l.bbox.minY<minY)minY=l.bbox.minY;
    if(l.bbox.maxX>maxX)maxX=l.bbox.maxX;
    if(l.bbox.maxY>maxY)maxY=l.bbox.maxY;
  }
  const pad=Math.max(maxX-minX,maxY-minY)*0.04;
  const svg=$('picker');
  svg.setAttribute('viewBox',`${minX-pad} ${minY-pad} ${maxX-minX+2*pad} ${maxY-minY+2*pad}`);
  svg.innerHTML='';
  // draw larger loops first so small ones stay clickable on top
  const order=[...loops].sort((a,b)=>b.area-a.area);
  for(const l of order){
    const p=document.createElementNS('http://www.w3.org/2000/svg','path');
    p.setAttribute('d','M'+l.pts.map(q=>q.x.toFixed(1)+','+q.y.toFixed(1)).join('L')+'Z');
    p.setAttribute('class','lp r-'+l.role);
    p.setAttribute('vector-effect','non-scaling-stroke');
    p.dataset.id=l.id;
    svg.appendChild(p);
  }
}

let clickTimer=null;
$('picker').addEventListener('click',e=>{
  const t=e.target.closest('.lp'); if(!t) return;
  const id=+t.dataset.id;
  clearTimeout(clickTimer);
  clickTimer=setTimeout(()=>{
    const l=loops.find(x=>x.id===id);
    if(!l) return;
    if(primaryId===null){
      // nothing selected — this tap chooses the main shape
      primaryId=id;
      reclassify();
    } else if(l.id===primaryId){
      // tap the main shape to deselect it
      primaryId=null;
      reclassify();
    } else if(l.role==='blocked'){
      return;
    } else if(l.rel==='inside'){
      l.role = l.role==='hole' ? 'ignore' : 'hole';
    } else if(l.rel==='outside'){
      l.role = l.role==='piece' ? 'ignore' : 'piece';
    }
    markCustomized('picker');
    renderPicker();
    update();
  },260);
});
$('picker').addEventListener('dblclick',e=>{
  const t=e.target.closest('.lp'); if(!t) return;
  clearTimeout(clickTimer);
  const id=+t.dataset.id;
  const l=loops.find(x=>x.id===id);
  if(!l||l.id===primaryId) return;
  primaryId=id;
  reclassify();
  markCustomized('picker');
  renderPicker();
  update();
});

// ---------- build + render ----------

const SLIDER_CFG={
  size: {mm:[30,600,1],     in:[1.25,24,0.125]},
  depth:{mm:[5,100,1],      in:[0.25,4,0.125]},
  tab:  {mm:[3,15,0.5],     in:[0.125,0.625,0.0625]}
};

function toMM(v){ return unit==='in' ? v*IN : v; }

function currentOpts(){
  return {
    size:toMM(+$('size').value),
    depth:toMM(+$('depth').value),
    pieces:+$('pieces').value,
    tabD:toMM(+$('tab').value)
  };
}

// reconfigure sliders so the step lands on clean values in the active unit
// (e.g. exactly 6in, not 5.98) — value converts and snaps on switch
function applyUnit(newUnit){
  if(newUnit===unit) return;
  for(const id of Object.keys(SLIDER_CFG)){
    const el=$(id);
    const mm = unit==='in' ? +el.value*IN : +el.value;
    const [lo,hi,st]=SLIDER_CFG[id][newUnit];
    let v = newUnit==='in' ? mm/IN : mm;
    v=Math.min(hi, Math.max(lo, Math.round(v/st)*st));
    el.min=lo; el.max=hi; el.step=st; el.value=v;
  }
  unit=newUnit;
}

function update(){
  const o=currentOpts();
  $('sizeOut').textContent=fmt(o.size);
  $('depthOut').textContent=fmt(o.depth);
  $('piecesOut').textContent=o.pieces;
  $('tabOut').textContent=fmt(o.tabD);

  const P=loops.find(l=>l.id===primaryId);
  if(!P){
    lastSVG=null; lastStats=null;
    $('preview').innerHTML='<div style="color:var(--ink-soft);font-size:14px;font-weight:600;text-align:center;padding:40px 20px">No main shape selected.<br>Tap an outline in the picker to choose one.</div>';
    $('stats').innerHTML='';
    return;
  }
  const scale=o.size/P.bbox.w;
  const xf=(pts,bb)=>pts.map(p=>({x:(p.x-bb.minX)*scale, y:(p.y-bb.minY)*scale}));

  const shapeDefs=[
    {outline:P, holes:loops.filter(l=>l.role==='hole')},
    ...loops.filter(l=>l.role==='piece').map(l=>({outline:l, holes:[]}))
  ];
  const MIN_SEG=9.525;   // 3/8in — minimum glueable segment; faces, folds
  const shapes=shapeDefs.map(sd=>{   // and tabs all derive from the merged polygon
    const bb=sd.outline.bbox;
    return buildShape(
      enforceMinSegment(xf(sd.outline.pts,bb), MIN_SEG),
      sd.holes.map(h=>enforceMinSegment(xf(h.pts,bb), MIN_SEG)),
      o);
  });

  const out=renderSVG(shapes, o.depth, o.tabD, fmt(o.size), foldStyle, true);
  const clean=renderSVG(shapes, o.depth, o.tabD, null, foldStyle, false);
  lastSVG=clean.markup;
  $('preview').innerHTML=out.markup;

  const holeCount=loops.filter(l=>l.role==='hole').length;
  const pieceCount=shapes.length;
  const primPat=shapes[0];
  let stripCount=0;
  shapes.forEach(s=>s.rings.forEach(r=>stripCount+=r.strips.length));
  lastStats={strips:stripCount,holes:holeCount,shapes:pieceCount,widthMM:o.size,depthMM:o.depth};
  $('piecesOut').textContent=stripCount;
  $('stats').innerHTML=
    `Shapes: <b>${pieceCount}</b>${holeCount?` &nbsp;Holes: <b>${holeCount}</b>`:''}<br>`+
    `Main perimeter: <b>${fmt(primPat.rings[0].perim)}</b><br>`+
    `Wall strips: <b>${stripCount}</b><br>`+
    `Sheet: <b>${fmt(out.W)} × ${fmt(out.H)}</b>`;
}

// ---------- wiring ----------

function loadFile(file){
  if(!file) return;
  // box-it-letters.js sets window.BX_LETTER=true around the change event it fires for a
  // letter pick; read it now (synchronously) — a letter is not an upload.
  const letter=window.BX_LETTER===true;
  const r=new FileReader();
  r.onload=()=>{
    try{
      const found=importSVG(r.result);
      inputSource=letter?'letter':'upload'; lastFileName=file.name;
      $('err').classList.remove('show');
      setLoops(found, file.name.replace(/\.svg$/i,''));
      if(found.dropped && window.BT && BT.toast) BT.toast('Kept the '+found.length+' biggest outlines of '+found.total+'. Small details were left out.');
      if(!letter) track('bx_file_upload',{input_source:'upload',outlines:found.total,
        outlines_dropped:found.dropped||0,file_kb:Math.round((file.size||0)/1024)});
      trackStarted();
    }catch(e){
      $('err').textContent=e.message;
      $('err').classList.add('show');
      track('bx_upload_error',{message:String(e.message).slice(0,90),input_source:letter?'letter':'upload'});
    }
  };
  r.readAsText(file);
}

['size','depth','pieces','tab'].forEach(id=>$(id).addEventListener('input',()=>{update();markCustomized(id);}));

$('sample').addEventListener('click',loadSampleClick);

const drop=$('drop');
drop.addEventListener('click',()=>$('file').click());
drop.addEventListener('keydown',e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();$('file').click();}});
$('file').addEventListener('change',e=>loadFile(e.target.files[0]));
['dragover','dragenter'].forEach(ev=>drop.addEventListener(ev,e=>{e.preventDefault();drop.classList.add('over');}));
['dragleave','drop'].forEach(ev=>drop.addEventListener(ev,e=>{e.preventDefault();drop.classList.remove('over');}));
drop.addEventListener('drop',e=>loadFile(e.dataTransfer.files[0]));

$('unitMm').addEventListener('click',()=>{markCustomized('unitMm');applyUnit('mm');$('unitMm').setAttribute('aria-pressed','true');$('unitIn').setAttribute('aria-pressed','false');update();});
$('unitIn').addEventListener('click',()=>{markCustomized('unitIn');applyUnit('in');$('unitIn').setAttribute('aria-pressed','true');$('unitMm').setAttribute('aria-pressed','false');update();});

$('foldDash').addEventListener('click',()=>{markCustomized('foldDash');foldStyle='perf';$('foldDash').setAttribute('aria-pressed','true');$('foldSolid').setAttribute('aria-pressed','false');document.getElementById('boxit-root').classList.remove('fold-solid');update();});
$('foldSolid').addEventListener('click',()=>{markCustomized('foldSolid');foldStyle='solid';$('foldSolid').setAttribute('aria-pressed','true');$('foldDash').setAttribute('aria-pressed','false');document.getElementById('boxit-root').classList.add('fold-solid');update();});

$('download').addEventListener('click',runDownload);

loadSample();
track('bx_tool_view',{signed_in:signedInCustomer()?1:0,returning:isUnlocked()?1:0});
})();